import type { NodeSchedulerCheckpoint } from './SimulationCheckpoint';
import {
  scheduleSimulationTask,
  restoreSimulationTask,
  captureSimulationTask,
  type SimulationTaskCheckpoint,
} from './scheduleSimulationTask';

export interface SimulationTiming {
  now(): number;
  /** Native-only continuation hooks; absent for passive/standalone timings. */
  resumable?: boolean;
  restoredScheduler?: NodeSchedulerCheckpoint;
  /** Stable logical-clock domain, independent of elapsed-time reset generations. */
  clockOwner?: object;
  /** Convert a wall-domain saved deadline into this owner's current clock domain. */
  fromWallTime?(at: number, wallAt: number): number;
  /** Scheduling wall in the same epoch domain as now(); optional for legacy callers. */
  wallNow?(): number;
  /** Capture a numeric-only mapper without retaining the live simulation. */
  captureTimeMapping?(): (at: number, wallAt: number) => number;
  schedule(at: number, callback: () => void): () => void;
}

export interface SimulationEventQueuePolicy {
  isUnpaced(): boolean;
  wallNow(): number;
}

interface TimedEvent {
  at: number;
  callback: () => void;
  active: boolean;
  node?: string;
}

/** Replay transport-delay events in timestamp order, between propagation waves. */
export class SimulationEventQueue {
  private events = new Set<TimedEvent>();
  private cancelWake?: () => void;
  private dispatching = false;
  private suspended = false;

  suspend() {
    if (this.dispatching) throw Error('Cannot suspend inside timed dispatch');
    this.suspended = true;
    this.cancelWake?.();
    this.cancelWake = undefined;
  }
  resume(wake?: SimulationTaskCheckpoint | null) {
    this.suspended = false;
    this.wake(wake ?? undefined);
  }
  captureWake() {
    return captureSimulationTask(this.cancelWake);
  }
  clear() {
    this.suspend();
    for (const event of this.events) event.active = false;
    this.events.clear();
  }
  capture() {
    if (this.dispatching) throw Error('Cannot capture inside timed dispatch');
    return [...this.events].map((event) => {
      if (!event.node || !Number.isFinite(event.at))
        throw Error('Unserializable timed event');
      return { node: event.node, at: event.at };
    });
  }
  restoreOrder(records: { node: string; at: number }[]) {
    if (!this.suspended) throw Error('Restore requires suspended event queue');
    const issued = [...this.events];
    if (issued.length !== records.length) throw Error('Restored event count mismatch');
    const ordered = records.map((record) => {
      const matches = issued.filter(
        (event) => event.node === record.node && event.at === record.at,
      );
      if (matches.length !== 1) throw Error('Restored event identity mismatch');
      return matches[0];
    });
    if (new Set(ordered).size !== issued.length) throw Error('Duplicate restored event');
    this.events = new Set(ordered);
  }

  constructor(
    private isBusy: () => boolean,
    private dispatch: (at: number, callbacks: Array<() => void>) => void,
    private policy?: SimulationEventQueuePolicy,
  ) {}

  schedule = (at: number, callback: () => void, node?: string) => {
    const event = { at, callback, active: true, node };
    this.events.add(event);
    this.wake();
    return () => {
      event.active = false;
      this.events.delete(event);
      this.wake();
    };
  };

  private nextTime() {
    let next = Infinity;
    for (const event of this.events) next = Math.min(next, event.at);
    return next;
  }

  private dispatchNext(at: number) {
    const callbacks: Array<() => void> = [];
    for (const event of this.events) {
      if (event.at !== at) continue;
      this.events.delete(event);
      callbacks.push(() => {
        if (event.active) event.callback();
      });
    }
    this.dispatch(at, callbacks);
  }

  wake(restored?: SimulationTaskCheckpoint) {
    if (this.dispatching || this.suspended) return;
    this.cancelWake?.();
    this.cancelWake = undefined;
    if (this.isBusy() || this.events.size === 0) return;
    const at = this.nextTime();
    if (this.policy?.isUnpaced() && !Number.isFinite(at)) return;
    const callback = () => {
      this.cancelWake = undefined;
      if (this.isBusy()) return;
      const next = this.nextTime();
      if (this.policy?.isUnpaced() && !Number.isFinite(next)) return;
      if (!(this.policy?.isUnpaced() ?? false) && next > this.wallNow()) {
        this.wake();
        return;
      }
      this.dispatching = true;
      try {
        this.dispatchNext(next);
      } finally {
        this.dispatching = false;
        this.wake();
      }
    };
    this.cancelWake = restored
      ? restoreSimulationTask(callback, restored, this.policy?.isUnpaced() ?? false)
      : scheduleSimulationTask(
          callback,
          (this.policy?.isUnpaced() ?? false) ? 0 : Math.max(0, at - this.wallNow()),
          this.policy?.isUnpaced() ?? false,
        );
  }

  private wallNow() {
    return this.policy?.wallNow() ?? Date.now();
  }

  /** Earlier timed events must settle before a newly received external input. */
  catchUp(until: number, settle: () => boolean | void) {
    if (this.dispatching || this.suspended) return;
    this.cancelWake?.();
    this.cancelWake = undefined;
    this.dispatching = true;
    try {
      // Bound catch-up for feedback circuits with zero-duration delays.
      for (let count = 0; count < 1000 && !this.isBusy(); count++) {
        const at = this.nextTime();
        if (this.policy?.isUnpaced() && !Number.isFinite(at)) break;
        if (at > until) break;
        this.dispatchNext(at);
        if (settle() === false) break;
      }
    } finally {
      this.dispatching = false;
      this.wake();
    }
  }
}
